"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@/types/user";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Mail,
  Phone,
  School,
  Calendar,
  GraduationCap,
  Award,
  Edit,
  Trash2
} from "lucide-react";
import { EditUserDialog } from "./edit-user-dialog";
import { DeleteUserDialog } from "./delete-user-dialog";

interface UserDetailDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailDialog({ user, open, onOpenChange }: UserDetailDialogProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();

  if (!user) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Dialog open={open && !editOpen && !deleteOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {user.firstName} {user.lastName}
            </DialogTitle>
            <DialogDescription>
              User ID: {user.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {user.isAmbassador && (
                <Badge variant="default" className="flex items-center gap-1">
                  <Award className="h-3 w-3" />
                  Ambassador
                </Badge>
              )}
              <Badge variant="outline">
                {user.schoolId ?? "General user (no school)"}
              </Badge>
            </div>

            <Separator />

            <div className="grid gap-4">
              {user.email && (
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              )}

              {user.phoneNumber && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Phone Number</p>
                    <p className="text-sm text-muted-foreground">{user.phoneNumber}</p>
                  </div>
                </div>
              )}

              {user.schoolId ? (
                <div
                  className="flex items-start gap-3 cursor-pointer rounded-lg p-2 -m-2 hover:bg-accent transition-colors"
                  onClick={() => {
                    navigate(`/dashboard/schools/${user.schoolId}`);
                    onOpenChange(false);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/dashboard/schools/${user.schoolId}`);
                      onOpenChange(false);
                    }
                  }}
                >
                  <School className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">School ID</p>
                    <p className="text-sm text-primary hover:underline">{user.schoolId}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <School className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">School ID</p>
                    <p className="text-sm text-muted-foreground">
                      Not assigned to a school
                    </p>
                  </div>
                </div>
              )}

              {user.birthday && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Birthday</p>
                    <p className="text-sm text-muted-foreground">{formatDate(user.birthday)}</p>
                  </div>
                </div>
              )}

              {user.graduationYear && (
                <div className="flex items-start gap-3">
                  <GraduationCap className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Graduation Year</p>
                    <p className="text-sm text-muted-foreground">Class of {user.graduationYear}</p>
                  </div>
                </div>
              )}
            </div>

            {(user.createdAt || user.updatedAt) && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                  {user.createdAt && (
                    <div>
                      <p className="font-medium">Created</p>
                      <p>{formatDate(user.createdAt)}</p>
                    </div>
                  )}
                  {user.updatedAt && (
                    <div>
                      <p className="font-medium">Last Updated</p>
                      <p>{formatDate(user.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
            <Button
              variant="default"
              onClick={() => setEditOpen(true)}
              className="gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditUserDialog
        user={user}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => {
          setEditOpen(false);
          onOpenChange(false);
        }}
      />

      <DeleteUserDialog
        user={user}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onSuccess={() => {
          // The user no longer exists, so close the detail view behind us.
          onOpenChange(false);
        }}
      />
    </>
  );
}

